<template>
  <div
    class="catalogSubsetProductsListItem"
    :class="[
      'catalogSubsetProductsListItem',
      {
        'catalogSubsetProductsListItem--isFromMyOffer':
          product.isFromMyOffer || product.isFromCatalogSubset,
      },
    ]"
  >
    <NuxtLink
      class="catalogSubsetProductsListItem__image"
      :to="localePath({ name: 'product-slug', params: { slug: product.slug } })"
    >
      <new-image-box
        :alt="product.name"
        background-color="#fff"
        :src="product.mainPictureUrl"
        :width="250"
        fit-contain-center
        :height="250"
        image-params="w_250,h_250"
      />
    </NuxtLink>
    <div class="catalogSubsetProductsListItem__info">
      <div v-if="product.isFromMyOffer" class="catalogSubsetProductsListItem__badge">Moja cena</div>
      <h3 class="catalogSubsetProductsListItem__name">
        <NuxtLink :to="localePath({ name: 'product-slug', params: { slug: product.slug } })"
          >{{ product.name }}
        </NuxtLink>
      </h3>
      <div class="catalogSubsetProductsListItem__tags">
        <div>
          Kod: <b>{{ product.code }}</b>
        </div>
        <div v-if="product.canBeOrdered && product.availableStock">
          Dostępnych
          <b>{{ product.availableStock }} {{ product.uom }}</b>
        </div>
      </div>
    </div>
    <div class="catalogSubsetProductsListItem__price">
      <b>
        {{
          showNetPrices ? product.priceObject.netFormatted : product.priceObject.grossFormatted
        }}</b
      >
      {{ product.priceObject.currencyPostfix }}
      {{ showNetPrices ? $t('net') : $t('gross') }}
    </div>
    <div class="catalogSubsetProductsListItem__button">
      <manual-add-to-basket
        v-if="!product.unavailabilityReason"
        :uom="product.uom !== 'paleta' ? product.uom : ''"
        @addToBasket="val => cartButtonClickHandler(product, val)"
        button-text="Do koszyka"
        @change="$emit('change', $event)"
        :can-be-ordered="product.canBeOrdered"
      />
      <div v-if="product.unavailabilityReason" class="catalogSubsetProductsListItem__buttonInfo">
        {{ product.unavailabilityReason.message }}
      </div>
    </div>
    <div class="catalogSubsetProductsListItem__checkbox">
      <slot name="checkbox" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import cartMixin from '~/mixins/cart';
import NewImageBox from '@/components/NewImageBox.vue';
import ManualAddToBasket from '@/components/ManualAddToBasket.vue';
import FormCheckboxSimple from '@/components/Form/FormCheckboxSimple.vue';

export default {
  name: 'CatalogSubsetProductsListItem',
  components: { FormCheckboxSimple, ManualAddToBasket, NewImageBox },
  mixins: [cartMixin],
  props: {
    product: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters('auth', ['showNetPrices', 'hasCatalogSubset']),
  },
  methods: {
    async cartButtonClickHandler(product, count) {
      this.isLoading = true;
      await this.addProductToCart(product, count);
      this.isLoading = false;
    },
  },
};
</script>

<style lang="scss">
.catalogSubsetProductsListItem {
  $root: &;
  display: flex;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 1.5rem 2rem 1.5rem 2rem;

  @include rwd('tablet') {
    flex-wrap: wrap;
  }

  &__image {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 11.5%;
    max-width: 11.5%;
    height: 10rem;

    @include rwd('tablet') {
      flex: 0 0 10rem;
      max-width: 10rem;
    }

    @include rwd('phone') {
      flex: 0 0 7rem;
      max-width: 7rem;
    }
  }

  &__info {
    flex: 0 0 38%;
    max-width: 38%;
    padding-left: 1rem;
    padding-right: 4rem;

    @include rwd('laptop') {
      flex: 0 0 30%;
      max-width: 30%;
      padding-right: 2.5rem;
    }

    @include rwd('tablet') {
      flex: 0 0 calc(100% - 10rem);
      max-width: calc(100% - 10rem);
    }

    @include rwd('phone') {
      flex: 0 0 calc(100% - 7rem);
      max-width: calc(100% - 7rem);
    }
  }

  a {
    transition: color $tr;

    &:hover {
      color: $cSec;
    }
  }

  &__name {
    font-size: 1.4rem;
    line-height: 1.42;
    color: $cMainNeutral;
  }

  &__tags {
    display: flex;
    margin-top: 0.6rem;

    b,
    strong {
      font-weight: 600;
    }

    div {
      padding: 0 1rem;
      position: relative;
      font-size: 1.2rem;
      line-height: 1.66;

      @include rwd('large-phone') {
        padding: 0 0.5rem;
      }

      &:before {
        content: '';
        display: block;
        width: 1px;
        height: 2rem;
        background-color: $cAddLight;
        margin-right: 1rem;
        position: absolute;
        left: 0;
        top: 0;
      }

      &:first-child {
        padding-left: 0;
        &:before {
          display: none;
        }
      }
    }
  }

  &__price {
    flex: 0 0 13%;
    max-width: 13%;
    color: $cMainNeutral;
    font-size: 1.6rem;
    line-height: 1.25;

    @include rwd('tablet') {
      flex: 0 0 100%;
      max-width: 100%;
      padding-left: 11rem;
      margin-bottom: 1.5rem;
      padding-top: 1rem;
    }

    @include rwd('phone') {
      padding-left: 8rem;
    }

    b,
    strong {
      font-size: 1.8rem;
      font-weight: 600;
    }
  }

  &__button {
    flex: 0 0 1;
    margin-left: auto;
  }

  &__buttonInfo {
    font-size: 1.4rem;
    line-height: 1.66;
    color: $cMainNeutral;
    padding: 0.5rem 0;
    font-weight: 600;
    margin-left: auto;
  }

  &__checkbox {
    flex: 0 0 4rem;
    max-width: 4rem;
    padding-left: 2rem;
    padding-bottom: 1.6rem;

    @include rwd('tablet') {
      position: absolute;
      right: 2rem;
      top: 1.5rem;
    }
  }

  &--isFromMyOffer {
    border: 1px solid $cMain;
  }

  &__badge {
    display: inline-flex;
    color: $cWhite;
    border-radius: 50px;
    font-weight: 700;
    padding: 0.3rem 1rem;
    background-color: $cMain;
    margin-bottom: 0.5rem;
  }
}
</style>
